import React, { useEffect, useState, lazy } from 'react';
/* ─── UTILITY: PriceDisplay with locale detection ─── */
function useCurrencySymbol() {
  const [symbol, setSymbol] = useState('$');
  useEffect(() => {
    try {
      const locale = navigator.language || 'es-AR';
      if (locale.startsWith('en-GB') || locale.startsWith('en-UK')) {
        setSymbol('£');
      } else if (locale.startsWith('de') || locale.startsWith('fr') || locale.startsWith('it') || locale.startsWith('pt-PT') || locale.startsWith('nl') || locale.startsWith('es-ES')) {
        setSymbol('€');
      } else {
        setSymbol('$');
      }
    } catch {
      setSymbol('$');
    }
  }, []);
  return symbol;
}
export function PriceDisplay({
  amount
}: {
  amount: number;
}) {
  const symbol = useCurrencySymbol();
  const formatted = amount.toLocaleString('es-AR');
  return <span data-id="element-890">
      {symbol}
      {formatted}
    </span>;
}
/* ─── STICKY ALERT BAR — HIGH-CONTRAST STATIC RED ─── */
export function AlertBarLP3() {
  return <>
      <style data-id="element-891">{`
      @keyframes marquee-scroll {
        0% { transform: translateX(0%); }
        100% { transform: translateX(-50%); }
      }
    `}</style>
      <div className="fixed top-0 left-0 right-0 z-[9999] text-white text-[12px] sm:text-[14px] font-bold py-2.5 w-full overflow-hidden" style={{
      background: '#CC0000',
      WebkitBackfaceVisibility: 'hidden',
      backfaceVisibility: 'hidden'
    }} data-id="element-892">
        <div className="whitespace-nowrap inline-flex" style={{
        animation: 'marquee-scroll 14s linear infinite'
      }} data-id="element-893">
          <span className="px-8" data-id="element-894">
            ⚠️ LA SALUD DE TU HIJO NO PUEDE ESPERAR – OFERTA TERMINA HOY
          </span>
          <span className="px-8" data-id="element-895">
            ⚠️ LA SALUD DE TU HIJO NO PUEDE ESPERAR – OFERTA TERMINA HOY
          </span>
          <span className="px-8" data-id="element-896">
            ⚠️ LA SALUD DE TU HIJO NO PUEDE ESPERAR – OFERTA TERMINA HOY
          </span>
          <span className="px-8" data-id="element-897">
            ⚠️ LA SALUD DE TU HIJO NO PUEDE ESPERAR – OFERTA TERMINA HOY
          </span>
        </div>
      </div>
    </>;
}
/* ─── HERO LP3 — MEDICAL FEAR ANGLE WITH URGENCY POLISH ─── */
export function HeroLP3() {
  return <section className="relative overflow-hidden bg-[#0f0f1a]" data-id="element-898">
      <style data-id="element-899">{`
        @keyframes pulse-red-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(220,38,38,0.3); }
          50% { box-shadow: 0 0 40px rgba(220,38,38,0.6), 0 0 80px rgba(220,38,38,0.2); }
        }
        .pulse-red-cta { animation: pulse-red-glow 2.5s ease-in-out infinite; }
        @keyframes pulse-orange-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(255,107,0,0.4); }
          50% { box-shadow: 0 0 40px rgba(255,107,0,0.7), 0 0 80px rgba(255,107,0,0.25); }
        }
        .pulse-orange-cta { animation: pulse-orange-glow 2.5s ease-in-out infinite; }
      `}</style>

      {/* Hero text — compact on mobile */}
      <div className="px-5 sm:px-6 pt-4 sm:pt-8 pb-2 sm:pb-6" data-id="element-900">
        <div className="max-w-[680px] mx-auto text-center" data-id="element-901">
          <div className="inline-block bg-red-900/60 border border-red-500/40 text-red-300 sm:text-[11px] font-black tracking-widest uppercase py-1.5 px-4 rounded-full mb-3 sm:mb-5 backdrop-blur-sm text-[9px]" data-id="element-902">
            🚨 ALERTA NUTRICIONAL — NIÑOS DE 3 A 12 AÑOS
          </div>

          <h1 className="text-[22px] sm:text-[32px] md:text-[38px] font-[900] leading-[1.15] mb-2 sm:mb-5 tracking-tight" style={{
          fontFamily: "'Merriweather', serif"
        }} data-id="element-903">
            <span className="text-white" data-id="element-904">¿Tu hijo rechaza </span>
            <span style={{
            color: '#FF0000'
          }} data-id="element-905">
              TODA
            </span>
            <span className="text-white" data-id="element-906">
              {' '}
              la comida saludable y cada cena es un{' '}
            </span>
            <span style={{
            color: '#FF0000'
          }} data-id="element-907">
              BERRINCHE
            </span>
            <span className="text-white" data-id="element-908">? 🥗🚫</span>
          </h1>
        </div>
      </div>

      {/* Hero image — shorter on mobile */}
      <div className="relative" data-id="element-909">
        <img src="https://cdn.magicpatterns.com/uploads/9HY8VEorUWMv3uChi7Z1wC/Cenas_bajo_tension.png" alt="Niño rechazando comida saludable en la mesa" className="w-full h-[200px] sm:h-[360px] object-cover object-center" loading="eager" data-id="element-910" />
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(to bottom, rgba(15,15,26,0.10) 0%, rgba(15,15,26,0.30) 60%, rgba(15,15,26,1) 100%)'
      }} data-id="element-911" />
      </div>

      {/* CTA + trust section — tight on mobile */}
      <div className="px-5 sm:px-6 pt-3 sm:pt-6 pb-6 sm:pb-10" data-id="element-912">
        <div className="max-w-[680px] mx-auto text-center" data-id="element-913">
          <p className="text-white text-[13px] sm:text-[16px] font-[600] leading-snug sm:leading-relaxed max-w-[540px] mx-auto mb-3 sm:mb-6" style={{
          fontFamily: "'Nunito', sans-serif"
        }} data-id="element-914">
            Lo que parece una "maña" es en realidad una{' '}
            <strong style={{
            color: '#F5C842'
          }} data-id="element-915">
              alarma de su cuerpo
            </strong>
            . Mientras él cierra la boca, su desarrollo se frena por falta de{' '}
            <strong style={{
            color: '#F5C842'
          }} data-id="element-916">
              hierro y nutrientes críticos
            </strong>
            .
          </p>

          <p className="text-[#5E8D5A] text-[12px] sm:text-[15px] font-bold mb-3 sm:mb-5" style={{
          fontFamily: "'Nunito', sans-serif"
        }} data-id="element-917">
            Descubrí el Método 3C para integrar nutrición invisible hoy mismo.
          </p>

          {/* Social proof — bigger stars, white text */}
          <div className="flex flex-col items-center gap-1.5 sm:gap-3 mb-3 sm:mb-6" data-id="element-918">
            <div className="flex items-center gap-2" data-id="element-919">
              <span className="text-yellow-400 text-[22px] sm:text-[22px]" data-id="element-920">
                ⭐⭐⭐⭐⭐
              </span>
              <span className="font-extrabold text-white text-sm" data-id="element-921">4.9/5</span>
            </div>
            <p className="text-white text-[12px] font-semibold" data-id="element-922">
              +1.200 madres evitaron la anemia silenciosa este mes
            </p>
          </div>

          {/* CTA */}
          <div className="max-w-[420px] mx-auto" data-id="element-923">
            <a href="#order" className="pulse-orange-cta text-white font-black py-4 px-6 rounded-2xl block w-full transition-all duration-200 text-[15px] sm:text-[17px] text-center no-underline" style={{
            background: '#FF6B00'
          }} data-id="element-924">
              SÍ, QUIERO EL MÉTODO 3C — ACCESO INMEDIATO
            </a>

            {/* Trust badges — white, single row, spaced */}
            <p className="text-white text-[11px] font-semibold flex gap-4 justify-center mt-3" data-id="element-925">
              <span data-id="element-926">📥 Acceso inmediato</span>
              <span data-id="element-927">🛡️ Garantía 7 días</span>
              <span data-id="element-928">💳 Pago seguro</span>
            </p>
          </div>
        </div>
      </div>
    </section>;
}
/* ─── SALES LETTER CONTAINER ─── */
export function SalesLetterSection() {
  return <section className="bg-[#FEFCF8] py-10 px-5 sm:py-14 sm:px-6" data-id="element-929">
      <div className="max-w-[640px] mx-auto" style={{
      fontFamily: "'Nunito', sans-serif"
    }} data-id="element-930">
        {/* Opening hook */}
        <div className="text-center mb-10" data-id="element-931">
          <p className="text-red-600 text-[13px] font-black tracking-widest uppercase mb-3" data-id="element-932">
            💔 HISTORIA REAL
          </p>
          <h2 className="text-[#1a1a2e] text-[22px] sm:text-[28px] font-[900] leading-tight tracking-tight mb-4" style={{
          fontFamily: "'Merriweather', serif"
        }} data-id="element-933">
            "Mi hijo se desmayó en el recreo... y fue mi culpa"
          </h2>
        </div>

        {/* Story paragraphs */}
        <div className="space-y-5 text-[#3a3a4a] text-[15px] sm:text-[16px] leading-[1.75]" data-id="element-934">
          <p data-id="element-935">
            Como madre, nada te hiela más la sangre que ver a tu hijo sin
            energía, pálido y débil, mientras los otros chicos corren. Yo
            pensaba que <em data-id="element-936">"ya iba a crecer"</em> y que sus berrinches eran
            normales.
          </p>

          <p className="text-[#1a1a2e] font-black text-[17px] sm:text-[19px] text-center py-2" data-id="element-937">
            Estaba equivocada.
          </p>

          <p data-id="element-938">
            La selectividad alimentaria no es un capricho; es una{' '}
            <strong className="text-red-700" data-id="element-939">barrera psicológica</strong> que
            impide que el hierro, el zinc y las vitaminas lleguen a sus órganos
            en formación. Si tu hijo solo come fideos, galletitas y leche, su
            cuerpo está en{' '}
            <strong className="text-red-700" data-id="element-940">modo supervivencia</strong>.
          </p>
        </div>

        {/* Uploaded image — exhausted mom */}
        <div className="my-8 rounded-2xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.12)]" data-id="element-941">
          <img src="https://cdn.magicpatterns.com/uploads/iwyyAqEi5Z85MVTZ1kTvWn/cocinera_a_la_carta.png" alt="Mamá agotada en la cocina después de cocinar múltiples platos" className="w-full h-auto object-cover" loading="lazy" data-id="element-942" />
        </div>

        {/* Diagnosis section */}
        <div className="mt-10" data-id="element-943">
          <h3 className="text-[#1a1a2e] text-[22px] sm:text-[28px] font-[900] text-center mb-6" style={{
          fontFamily: "'Merriweather', serif"
        }} data-id="element-944">
            ¿Qué está pasando realmente en su cerebro?
          </h3>

          {/* Red pain bullets with bold SVG X icons */}
          <div className="space-y-4 mb-10" data-id="element-945">
            <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-4 sm:p-5" data-id="element-946">
              <div className="flex items-start gap-3" data-id="element-947">
                <svg className="w-8 h-8 flex-shrink-0 mt-0.5" viewBox="0 0 32 32" fill="none" data-id="element-948">
                  <circle cx="16" cy="16" r="16" fill="#DC2626" data-id="element-949" />
                  <path d="M11 11L21 21M21 11L11 21" stroke="white" strokeWidth="3" strokeLinecap="round" data-id="element-950" />
                </svg>
                <div data-id="element-951">
                  <h4 className="text-[#1a1a2e] text-[16px] sm:text-[18px] font-black mb-1" data-id="element-952">
                    Déficit Cognitivo
                  </h4>
                  <p className="text-[#555] text-[14px] leading-relaxed" data-id="element-953">
                    Menos concentración y retraso en el aprendizaje. Su cerebro
                    no recibe el combustible que necesita para desarrollarse.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-4 sm:p-5" data-id="element-954">
              <div className="flex items-start gap-3" data-id="element-955">
                <svg className="w-8 h-8 flex-shrink-0 mt-0.5" viewBox="0 0 32 32" fill="none" data-id="element-956">
                  <circle cx="16" cy="16" r="16" fill="#DC2626" data-id="element-957" />
                  <path d="M11 11L21 21M21 11L11 21" stroke="white" strokeWidth="3" strokeLinecap="round" data-id="element-958" />
                </svg>
                <div data-id="element-959">
                  <h4 className="text-[#1a1a2e] text-[16px] sm:text-[18px] font-black mb-1" data-id="element-960">
                    Anemia Crónica
                  </h4>
                  <p className="text-[#555] text-[14px] leading-relaxed" data-id="element-961">
                    Sistema inmune debilitado. Se enferma cada semana, falta al
                    jardín, no tiene defensas.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-4 sm:p-5" data-id="element-962">
              <div className="flex items-start gap-3" data-id="element-963">
                <svg className="w-8 h-8 flex-shrink-0 mt-0.5" viewBox="0 0 32 32" fill="none" data-id="element-964">
                  <circle cx="16" cy="16" r="16" fill="#DC2626" data-id="element-965" />
                  <path d="M11 11L21 21M21 11L11 21" stroke="white" strokeWidth="3" strokeLinecap="round" data-id="element-966" />
                </svg>
                <div data-id="element-967">
                  <h4 className="text-[#1a1a2e] text-[16px] sm:text-[18px] font-black mb-1" data-id="element-968">
                    Fatiga Extrema
                  </h4>
                  <p className="text-[#555] text-[14px] leading-relaxed" data-id="element-969">
                    Falta de combustible real para jugar y crecer. Lo ves
                    cansado, sin ganas, apagado.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Transition phrase + mini CTA */}
          <div className="text-center my-8" data-id="element-970">
            <p className="text-[17px] sm:text-[20px] font-[900] leading-snug mb-4" style={{
            color: '#F5C842'
          }} data-id="element-971">
              La buena noticia: Puedes revertir esto sin que él se de cuenta.
            </p>
            <a href="#order" className="inline-block text-white text-[14px] sm:text-[15px] font-black py-3 px-6 rounded-xl transition-all duration-200 hover:brightness-110" style={{
            background: '#FF6B00',
            boxShadow: '0 6px 20px rgba(255,107,0,0.35)'
          }} data-id="element-972">
              QUIERO EVITAR ESTO →
            </a>
          </div>

          {/* Transition to solution */}
          <div className="bg-[#0f0f1a] rounded-2xl p-6 sm:p-8 text-center" data-id="element-973">
            <p className="text-[#5E8D5A] text-[11px] font-black tracking-widest uppercase mb-3" data-id="element-974">
              LA SOLUCIÓN
            </p>
            <h3 className="text-white text-[22px] sm:text-[26px] font-[900] mb-4 leading-tight" style={{
            fontFamily: "'Merriweather', serif"
          }} data-id="element-975">
              Método 3C
            </h3>
            <p className="text-yellow-400 text-[15px] sm:text-[17px] font-bold mb-5" data-id="element-976">
              CAMUFLAR · CRUJIENTE · COLOR
            </p>
            <p className="text-gray-300 text-[14px] sm:text-[15px] leading-relaxed max-w-[480px] mx-auto mb-6" data-id="element-977">
              No lo obligues a comer. No pelees. Entrá por su radar cerebral.
              Vitalia no es un recetario de fotos lindas; es{' '}
              <strong className="text-white" data-id="element-978">técnica aplicada</strong> para que
              un cerebro selectivo acepte vegetales sin percibir "amenaza".
            </p>

            {/* Green solution bullets */}
            <div className="space-y-3 text-left max-w-[420px] mx-auto" data-id="element-979">
              <div className="flex items-start gap-3" data-id="element-980">
                <span className="text-[#5E8D5A] text-lg flex-shrink-0 mt-0.5" data-id="element-981">
                  ✅
                </span>
                <p className="text-white text-[14px] sm:text-[15px] font-semibold leading-snug" data-id="element-982">
                  Recetas Familiares con verdura "invisible" integrada.
                </p>
              </div>
              <div className="flex items-start gap-3" data-id="element-983">
                <span className="text-[#5E8D5A] text-lg flex-shrink-0 mt-0.5" data-id="element-984">
                  ✅
                </span>
                <p className="text-white text-[14px] sm:text-[15px] font-semibold leading-snug" data-id="element-985">
                  Técnica de introducción gradual para bajar la guardia del
                  niño.
                </p>
              </div>
              <div className="flex items-start gap-3" data-id="element-986">
                <span className="text-[#5E8D5A] text-lg flex-shrink-0 mt-0.5" data-id="element-987">
                  ✅
                </span>
                <p className="text-white text-[14px] sm:text-[15px] font-semibold leading-snug" data-id="element-988">
                  Resultados visibles en su vitalidad desde la primera semana.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
/* ─── PAIN PHOTO GALLERY (Uploaded Images) — Compact ─── */
export function PainGallerySection() {
  return <section className="bg-[#1a1a2e] py-6 px-5 sm:py-10 sm:px-6" data-id="element-989">
      <div className="max-w-[720px] mx-auto" data-id="element-990">
        <p className="text-red-400 text-[11px] font-black tracking-widest uppercase text-center mb-1" data-id="element-991">
          📸 LA REALIDAD DE MILES DE HOGARES
        </p>
        <h2 className="text-white text-[20px] sm:text-[26px] font-[900] text-center mb-4 sm:mb-6 leading-tight tracking-tight" style={{
        fontFamily: "'Merriweather', serif"
      }} data-id="element-992">
          ¿Te identificás con alguna?
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-4" data-id="element-993">
          {/* Photo 1 — Child rejecting broccoli */}
          <div className="rounded-xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.3)] relative" data-id="element-994">
            <img src="https://cdn.magicpatterns.com/uploads/dnRZp2ibP9MgZa8AkZreRE/pov3_image.png" alt="Niño rechazando brócoli en la cena" className="w-full h-[180px] sm:h-[300px] object-cover" loading="lazy" data-id="element-995" />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-3 sm:p-4" data-id="element-996">
              <p className="text-white text-[13px] font-bold leading-snug drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]" data-id="element-997">
                "No quiero, no me gusta, no lo voy a probar"
              </p>
            </div>
          </div>

          {/* Photo 2 — Exhausted mom */}
          <div className="rounded-xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.3)] relative" data-id="element-998">
            <img src="https://cdn.magicpatterns.com/uploads/cHcHtnz6CwdFJF5CvcM3GK/pov1_image.png" alt="Mamá agotada sosteniendo plato de comida rechazada" className="w-full h-[180px] sm:h-[300px] object-cover" loading="lazy" data-id="element-999" />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-3 sm:p-4" data-id="element-1000">
              <p className="text-white text-[13px] font-bold leading-snug drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]" data-id="element-1001">
                "Otra vez cocinando para la basura..."
              </p>
            </div>
          </div>

          {/* Photo 3 — Food in trash */}
          <div className="rounded-xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.3)] relative" data-id="element-1002">
            <img src="https://cdn.magicpatterns.com/uploads/po7JVZN6ybEF7g3c1w89Cm/platos_al_tacho.png" alt="Comida saludable siendo tirada a la basura" className="w-full h-[180px] sm:h-[300px] object-cover" loading="lazy" data-id="element-1003" />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-3 sm:p-4" data-id="element-1004">
              <p className="text-white text-[13px] font-bold leading-snug drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]" data-id="element-1005">
                "Tiempo, plata y amor... al tacho"
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}